/**
 * UI Context
 * 
 * Manages global UI state like active tab, dialogs, and notifications.
 * Separates UI state from data state to prevent unnecessary re-renders.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const UIContext = createContext();

export const UIProvider = ({ children }) => {
  // Main navigation
  const [activeTab, setActiveTab] = useState('clients');
  const [clientChartTab, setClientChartTab] = useState('overview');
  const [adminInnerTab, setAdminInnerTab] = useState('setup');
  
  // Global loading states
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // Notifications moved to NotificationsContext (Feb 2026) — they
  // polled every 30s and were forcing the entire Dashboard tree to
  // re-render through this provider.  See /context/NotificationsContext.jsx
  
  // Tasks
  const [tasks, setTasks] = useState([]);
  const [tasksAssignedByMe, setTasksAssignedByMe] = useState([]);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  
  // Search
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  
  // Dashboard stats
  const [stats, setStats] = useState({});
  
  // Alert counts
  const [alertCounts, setAlertCounts] = useState({});

  // Phase-1 stabilization (Feb 2026): wrap ``resetUIState`` in
  // ``useCallback`` with [] — it only calls setters from useState
  // (which are stable), so its identity can also be stable across
  // renders.  Without this, every UIProvider render produced a fresh
  // function reference and broke the ``useMemo(value)`` below.
  const resetUIState = useCallback(() => {
    setActiveTab('dashboard');
    setClientChartTab('overview');
    setAdminInnerTab('setup');
    setTasks([]);
    setTasksAssignedByMe([]);
    setShowTaskDialog(false);
    setGlobalSearchQuery('');
    setShowGlobalSearch(false);
    setStats({});
    setAlertCounts({});
  }, []);

  // Phase-1 stabilization (Feb 2026): memoize the Provider value.
  // This is the SINGLE biggest perf win in the app — UIContext is
  // consumed by the Dashboard component (App.js:1370) which owns
  // ~85k lines of JSX.  Before this memo, every keystroke that
  // touched any UI state (loading toast, notification arriving,
  // dialog opening) forced Dashboard to re-render its entire tree
  // because the Provider's ``value`` was a new object literal on
  // every render.  Now Dashboard re-renders only when one of the
  // listed fields actually changes.
  //
  // Setters from useState are stable across renders — intentionally
  // NOT in the deps array.  ``resetUIState`` is now useCallback-stable.
  const value = useMemo(() => ({
    // Navigation
    activeTab,
    setActiveTab,
    clientChartTab,
    setClientChartTab,
    adminInnerTab,
    setAdminInnerTab,
    
    // Loading
    isLoading,
    setIsLoading,
    loadingMessage,
    setLoadingMessage,
    
    // Tasks
    tasks,
    setTasks,
    tasksAssignedByMe,
    setTasksAssignedByMe,
    showTaskDialog,
    setShowTaskDialog,
    
    // Search
    globalSearchQuery,
    setGlobalSearchQuery,
    showGlobalSearch,
    setShowGlobalSearch,
    
    // Stats
    stats,
    setStats,
    
    // Alerts
    alertCounts,
    setAlertCounts,
    
    // Reset function
    resetUIState,
  }), [
    activeTab, clientChartTab, adminInnerTab,
    isLoading, loadingMessage,
    tasks, tasksAssignedByMe, showTaskDialog,
    globalSearchQuery, showGlobalSearch,
    stats, alertCounts,
    resetUIState,
  ]);

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within UIProvider');
  }
  return context;
};

export default UIContext;
